const ICONDIR_SIZE = 6
const ICONDIRENTRY_SIZE = 16

const canvasToPngBytes = (image: HTMLImageElement, size: number): Promise<Uint8Array> =>
  new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      reject(new Error('Canvas 2D context unavailable'))
      return
    }
    const imgW = image.naturalWidth || image.width
    const imgH = image.naturalHeight || image.height
    const scale = Math.min(size / imgW, size / imgH)
    const drawW = imgW * scale
    const drawH = imgH * scale
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(image, (size - drawW) / 2, (size - drawH) / 2, drawW, drawH)
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('canvas.toBlob returned null'))
        return
      }
      blob
        .arrayBuffer()
        .then((buf) => resolve(new Uint8Array(buf)))
        .catch(reject)
    }, 'image/png')
  })

/**
 * Encode one or more sizes as a single multi-resolution .ico file.
 * Modern ICO readers (Windows Vista+, macOS, browsers) accept PNG-compressed
 * frame payloads, so this is just an ICONDIR/ICONDIRENTRY container wrapping
 * PNGs produced by the browser's own encoder — no raw-DIB encoding needed.
 */
export const encodeIco = async (image: HTMLImageElement, sizes: readonly number[]): Promise<Blob> => {
  const frames = await Promise.all(sizes.map((size) => canvasToPngBytes(image, size)))

  const headerSize = ICONDIR_SIZE + ICONDIRENTRY_SIZE * frames.length
  const totalSize = headerSize + frames.reduce((sum, frame) => sum + frame.length, 0)

  const buffer = new Uint8Array(totalSize)
  const view = new DataView(buffer.buffer)

  view.setUint16(0, 0, true) // reserved
  view.setUint16(2, 1, true) // type = icon
  view.setUint16(4, frames.length, true)

  let dirOffset = ICONDIR_SIZE
  let dataOffset = headerSize
  frames.forEach((frame, i) => {
    const size = sizes[i]
    const dim = size >= 256 ? 0 : size // 0 means 256 per the ICO spec
    buffer[dirOffset] = dim // width
    buffer[dirOffset + 1] = dim // height
    buffer[dirOffset + 2] = 0 // color count (0 = no palette, PNG frame)
    buffer[dirOffset + 3] = 0 // reserved
    view.setUint16(dirOffset + 4, 1, true) // planes
    view.setUint16(dirOffset + 6, 32, true) // bit count
    view.setUint32(dirOffset + 8, frame.length, true) // bytes in resource
    view.setUint32(dirOffset + 12, dataOffset, true) // offset from file start
    buffer.set(frame, dataOffset)
    dataOffset += frame.length
    dirOffset += ICONDIRENTRY_SIZE
  })

  return new Blob([buffer], {type: 'image/x-icon'})
}
